// Anki-style scheduler (simplified SM-2 with learning steps).
// Card fields: state new|learning|review|relearning, step, ease, interval(days),
// reps, lapses, due(ISO), nextReview(date), lastReviewed(date).

const NEW_STEPS     = [1, 10];   // minutes
const RELEARN_STEPS = [10];      // minutes
const GRAD_IVL = 1;              // days, graduating (Good)
const EASY_IVL = 4;              // days, graduating (Easy)
const MIN_EASE = 1.3;
const HARD_MULT = 1.2;
const EASY_BONUS = 1.3;

export const GRADES = ['again', 'hard', 'good', 'easy'];

function defaults(card = {}) {
  const state = card.state
    ?? ((card.nextReview || card.interval) ? 'review' : 'new');
  return {
    ease: card.ease ?? card.easeFactor ?? 2.5,
    interval: card.interval ?? 0,
    reps: card.reps ?? card.repetitions ?? 0,
    lapses: card.lapses ?? 0,
    state, step: card.step ?? 0,
  };
}

function fmt(mins) {
  if (mins < 60) return `${Math.max(1, Math.round(mins))}分`;
  if (mins < 1440) return `${Math.round(mins / 60)}時`;
  const d = Math.round(mins / 1440);
  if (d < 30) return `${d}天`;
  if (d < 365) return `${Math.round(d / 30)}月`;
  return `${(d / 365).toFixed(1)}年`;
}

// Pure scheduling: returns { dueMins, ease, interval, state, step, reps, lapses }
function step(card, grade) {
  let { ease, interval, reps, lapses, state, step: s } = defaults(card);
  let dueMins;

  if (state === 'new' || state === 'learning') {
    const steps = NEW_STEPS;
    if (grade === 'again') { state = 'learning'; s = 0; dueMins = steps[0]; }
    else if (grade === 'hard') {
      state = 'learning';
      const cur = steps[Math.min(s, steps.length - 1)];
      // keep Hard between Again and Good so the buttons don't collapse
      dueMins = (cur <= steps[0] && steps.length > 1) ? Math.round((steps[0] + steps[1]) / 2) : cur;
    }
    else if (grade === 'good') {
      s += 1;
      if (s >= steps.length) { state = 'review'; interval = GRAD_IVL; dueMins = GRAD_IVL * 1440; }
      else { state = 'learning'; dueMins = steps[s]; }
    } else { state = 'review'; interval = EASY_IVL; dueMins = EASY_IVL * 1440; }
  } else if (state === 'relearning') {
    const steps = RELEARN_STEPS;
    if (grade === 'again') { s = 0; dueMins = steps[0]; }
    else if (grade === 'hard') { dueMins = Math.round(steps[Math.min(s, steps.length - 1)] * 1.5); }
    else if (grade === 'good') {
      s += 1;
      if (s >= steps.length) { state = 'review'; interval = Math.max(1, Math.round(interval)); dueMins = interval * 1440; }
      else dueMins = steps[s];
    } else {  // easy graduates bigger than good
      state = 'review'; interval = Math.max(EASY_IVL, Math.round(interval) + EASY_IVL - 1); dueMins = interval * 1440;
    }
  } else { // review
    if (grade === 'again') {
      lapses += 1; ease = Math.max(MIN_EASE, ease - 0.2);
      state = 'relearning'; s = 0; interval = 1; dueMins = RELEARN_STEPS[0];
    } else {
      // strictly increasing so Hard < Good < Easy even at tiny intervals
      const base = Math.max(1, interval);
      const hardIvl = Math.max(base + 1, Math.round(base * HARD_MULT));
      const goodIvl = Math.max(hardIvl + 1, Math.round(base * ease));
      const easyIvl = Math.max(goodIvl + 1, Math.round(base * ease * EASY_BONUS));
      if (grade === 'hard') { ease = Math.max(MIN_EASE, ease - 0.15); interval = hardIvl; }
      else if (grade === 'good') { interval = goodIvl; }
      else { ease += 0.15; interval = easyIvl; }
      dueMins = interval * 1440;
    }
    reps += 1;
  }
  return { dueMins, ease: Math.round(ease * 100) / 100, interval, state, step: s, reps, lapses };
}

// Apply a grade → full persisted card patch.
export function answer(card, grade) {
  const r = step(card, grade);
  const due = new Date(Date.now() + r.dueMins * 60000);
  return {
    ease: r.ease, interval: r.interval, reps: r.reps, lapses: r.lapses,
    state: r.state, step: r.step,
    due: due.toISOString(),
    nextReview: due.toISOString().split('T')[0],
    lastReviewed: new Date().toISOString().split('T')[0],
  };
}

// Label shown on each grade button (the resulting interval).
export function previewLabel(card, grade) {
  return fmt(step(card, grade).dueMins);
}

// Still being studied this session (didn't graduate to a day+ review)?
export function inLearning(cardPatch) {
  return cardPatch.state === 'learning' || cardPatch.state === 'relearning';
}

export function cardState(card) {
  return defaults(card).state;
}

export function isDue(card) {
  const d = defaults(card);
  if (d.state === 'new') return true;
  if (card.due) return new Date(card.due) <= new Date();
  if (card.nextReview) return card.nextReview <= new Date().toISOString().split('T')[0];
  return true;
}
